import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Alert, Box, Button, IconButton, Paper, Tooltip, Typography} from "@mui/material";
import MyLocationOutlined from "@mui/icons-material/MyLocationOutlined";
import {useTranslation} from "react-i18next";
import {Map as LibreMap, Marker, NavigationControl} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import {groupTrackingPoints, trackingName, trackingStateLabel} from "./tracking-state";
import "./courier-map.css";

export const courierMapStyle = process.env.REACT_APP_COURIER_MAP_STYLE_URL || "https://tiles.openfreemap.org/styles/liberty";

function fitCouriers(map, groups) {
    if (!groups.length) return false;
    const coordinates = groups.map(group => [group[0].location.longitude, group[0].location.latitude]);
    const bounds = coordinates.reduce((box, [lng, lat]) => [[Math.min(box[0][0], lng), Math.min(box[0][1], lat)],
        [Math.max(box[1][0], lng), Math.max(box[1][1], lat)]], [coordinates[0], coordinates[0]]);
    map.fitBounds(bounds, {padding: 60, maxZoom: 15, duration: 0});
    return true;
}

function markerDescription(group, t) {
    return group.map(point => `${trackingName(point)}: ${t(trackingStateLabel(point.state))}, ${point.progress}, `
        + `${t("CourierMapAccuracy", {accuracy: point.location.accuracy})}, ${point.location.capturedAt}`
        + (point.poorAccuracy ? `, ${t("CourierMapPoorAccuracy")}` : "")).join("; ");
}

export default function CourierMapCanvas({points, onDetails}) {
    const {t} = useTranslation();
    const container = useRef(null), map = useRef(null), markers = useRef(new Map());
    const camera = useRef(null), fitted = useRef(false);
    const [error, setError] = useState(false), [attempt, setAttempt] = useState(0), [ready, setReady] = useState(0);
    const [selected, setSelected] = useState(null);
    const groups = groupTrackingPoints(points);
    const currentGroups = useRef(groups), details = useRef(onDetails);
    currentGroups.current = groups; details.current = onDetails;

    useEffect(() => {
        let instance;
        try {
            instance = new LibreMap({container: container.current, style: courierMapStyle,
                center: camera.current?.center || [44.513, 40.181], zoom: camera.current?.zoom ?? 10,
                bearing: camera.current?.bearing || 0, pitch: camera.current?.pitch || 0,
                attributionControl: {compact: false}});
            map.current = instance;
            instance.addControl(new NavigationControl(), "top-right");
            instance.on("error", () => setError(true));
            instance.on("load", () => {setError(false); setReady(value => value + 1);});
            setReady(value => value + 1);
        } catch { setError(true); }
        const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => instance?.resize());
        resize?.observe(container.current);
        return () => {
            resize?.disconnect();
            if (instance) camera.current = {center: instance.getCenter(), zoom: instance.getZoom(),
                bearing: instance.getBearing(), pitch: instance.getPitch()};
            markers.current.forEach(marker => marker.remove()); markers.current.clear();
            instance?.remove(); map.current = null;
        };
    }, [attempt]);

    // Reconcile before paint so removed/expired locations cannot linger in the imperative map DOM.
    useLayoutEffect(() => {
        if (!map.current) return;
        const active = new Set();
        groups.forEach(group => {
            const location = group[0].location, key = `${location.longitude}:${location.latitude}`;
            active.add(key);
            let marker = markers.current.get(key);
            if (!marker) {
                const element = document.createElement("button"); element.type = "button";
                element.addEventListener("click", () => {
                    const matches = currentGroups.current.find(items => `${items[0].location.longitude}:${items[0].location.latitude}` === key);
                    if (matches?.length === 1) details.current(matches[0].courierId);
                    else setSelected(key);
                });
                marker = new Marker({element}).setLngLat([location.longitude, location.latitude]).addTo(map.current);
                markers.current.set(key, marker);
            }
            const element = marker.getElement();
            element.className = `courier-map-marker ${group.some(point => point.state === "stale") ? "stale" : "fresh"}`
                + (group.some(point => point.poorAccuracy) ? " poor-accuracy" : "");
            element.textContent = group.length > 1 ? String(group.length) : trackingName(group[0]).slice(0, 1) || "•";
            element.title = markerDescription(group, t); element.setAttribute("aria-label", element.title);
        });
        markers.current.forEach((marker, key) => {if (!active.has(key)) {marker.remove(); markers.current.delete(key);}});
        if (!fitted.current) fitted.current = fitCouriers(map.current, groups);
    }, [groups, ready, t]);

    const selection = groups.find(group => `${group[0].location.longitude}:${group[0].location.latitude}` === selected);
    return <Box>
        {error && <Alert severity="warning" action={<Button color="inherit" onClick={() => {
            setError(false); setAttempt(value => value + 1);
        }}>{t("Retry")}</Button>}>{t("CourierMapProviderFailed")}</Alert>}
        <Box sx={{position: "relative"}}>
            <Box ref={container} className="courier-map-canvas" role="region" aria-label={t("CourierMapTitle")}/>
            <Paper elevation={3} sx={{position: "absolute", top: 12, left: 12, borderRadius: 1, zIndex: 1}}>
                <Tooltip title={t("CourierMapRecenter")} placement="right">
                    <span>
                        <IconButton aria-label={t("CourierMapRecenter")} color="primary"
                            disabled={!groups.length || !map.current} onClick={() => fitCouriers(map.current, groups)}>
                            <MyLocationOutlined/>
                        </IconButton>
                    </span>
                </Tooltip>
            </Paper>
        </Box>
        {selection?.length > 1 && <Paper variant="outlined" sx={{p: 1}} role="group" aria-label={t("CourierMapSameLocation")}>
            <Typography>{t("CourierMapSameLocation")}</Typography>
            {selection.map(point => <Button key={point.courierId} onClick={() => onDetails(point.courierId)}>{trackingName(point)}</Button>)}
            <Button onClick={() => setSelected(null)}>{t("Close")}</Button>
        </Paper>}
    </Box>;
}
